import { type Href, Link, Stack } from "expo-router";
import { Compass } from "lucide-react-native";

import { Box } from "../ui/box";
import { Button, ButtonText } from "../ui/button";
import { Center } from "../ui/center";
import { Heading } from "../ui/heading";
import { Icon } from "../ui/icon";
import { Text } from "../ui/text";
import { VStack } from "../ui/vstack";

export type NotFoundScreenProps = {
  /** Where the "back to home" button navigates. Defaults to the app root. */
  homeHref?: Href;
  title?: string;
  description?: string;
};

/**
 * Shared 404 screen used by every app's `+not-found.tsx`. Rendered both for
 * unmatched routes and when a dynamic segment resolves to an invalid value
 * (e.g. a route group leaking in as a `[brandId]`), so no screen ever runs a
 * query against a garbage id.
 */
export const NotFoundScreen = ({
  homeHref = "/",
  title = "Página no encontrada",
  description = "La página que buscas no existe o ya no está disponible.",
}: NotFoundScreenProps) => {
  return (
    <>
      <Stack.Screen options={{ title: "No encontrado", headerShown: false }} />
      <Center className="flex-1 bg-background-0 px-6">
        <VStack space="lg" className="w-full max-w-[420px] items-center">
          <Box className="h-24 w-24 items-center justify-center rounded-full bg-brand-50">
            <Icon as={Compass} className="h-12 w-12 text-brand-500" />
          </Box>
          <Heading className="text-center font-bold text-typography-900">
            404
          </Heading>
          <Heading size="xl" className="text-center text-typography-900">
            {title}
          </Heading>
          <Text size="md" className="text-center text-typography-500">
            {description}
          </Text>
          <Link href={homeHref} replace asChild>
            <Button
              action="brand"
              variant="solid"
              size="xl"
              rounded="full"
              className="mt-2 w-full"
            >
              <ButtonText className="font-bold">Volver al inicio</ButtonText>
            </Button>
          </Link>
        </VStack>
      </Center>
    </>
  );
};

export default NotFoundScreen;
